<script lang="ts" setup>
import { SancareToggle } from '@sancare/typescript-commons'
import _ from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'

import CriteriaDisplayer from '@/filters/CriteriaDisplayer.vue'
import FilterBuilder from '@/filters/FilterBuilder.vue'
import { hasPermission, RoleEnum, savedSearchPermissions } from '@/misc/permissions/permissionsHelper'
import router from '@/router'
import { doesSearchIdExist, saveCurrentSavedSearch } from '@/saved-searches/helpers'
import SavedSearchDetails from '@/saved-searches/SavedSearchDetails.vue'
import ShowActiveSearchesModal from '@/saved-searches/ShowActiveSearchesModal.vue'
import { DEFAULT_SEARCH_ID } from '@/saved-searches/types'
import { canShareWithSancare, canShareWithTeam, savedSearchLabels } from '@/saved-searches/utils'
import StayListCriteriaParameters from '@/stay-list/StayListCriteriaParameters.vue'
import { StayListCriteriaProps } from '@/stay-list/types'
import { useStore } from '@/store'
import { CriterionCondition, ExistingCriterionConditionDto } from '@/store/modules/searches/types'
import { PrivacyEnum } from '@/store/modules/settings/types'
import { Search } from '@/store/modules/stay-saved-search/types'

const props = defineProps<StayListCriteriaProps>()

const emit = defineEmits<{
  'clear-message': []
  'set-quick-mode': [enabled: boolean]
}>()

const store = useStore()

const searchLabels = computed(() => savedSearchLabels(props.mode))

const searchError = ref<string | null>(null)
const searchSuccess = ref<string | null>(null)
const editingCriterion = ref<ExistingCriterionConditionDto | null>(null)

const shortHandStayListParams = computed(() => store.getters.shortHandStayListParams)

const params = computed(() => store.state.stayList.stayListParams)
const currentSearch = computed(() => store.state.stayList.currentSearch)

const addSavedSearchRequest = computed(() => store.state.savedSearches.addSavedSearchRequest)
const editSavedSearchRequest = computed(() => store.state.savedSearches.editSavedSearchRequest)
const fetchSavedSearchesRequest = computed(() => store.state.savedSearches.fetchSavedSearchesRequest)

const isSearchCreation = computed(() => store.state.stayList.stayListParams.searchCreation)
const isSearchEdition = computed(() => doesSearchIdExist(store.state.stayList.stayListParams.searchEdition))

const isAdmin = computed(() => store.state.login.userRole === 'ROLE_SANCARE_DEV')
const isSearchEditor = computed(() =>
  hasPermission(RoleEnum.ROLE_SANCARE_SEARCH_MANAGER, store.state.login.userRole)
)

const currentSearchEdited = computed(() => store.state.stayList.currentSearchEdited)
const currentUser = computed(() => store.state.login.currentUser)
const currentUsername = computed(() => store.state.login.username)

const editionInProgress = computed(() => store.state.stayList.editionInProgress)
const activeSearches = computed(() => {
  let searchIds = store.state.stayList.activeSearches
  const searchesList = store.state.savedSearches.savedSearches

  searchIds = Array.isArray(searchIds) ? searchIds : [searchIds]
  searchIds = searchIds.map((searchId: number | string) =>
    typeof searchId === 'number' ? searchId : parseInt(searchId, 10)
  )

  return searchesList.filter((search) => searchIds.includes(search.id))
})

const isOwner = computed(() =>
  currentSearch.value.user === null || currentSearch.value.user.username === currentUsername.value
)

const isSancareUser = computed(() => store.state.login.currentUser.sancareUser)

const activeSearchIds = computed(() => activeSearches.value.map((s) => s.id))

const isRefreshUrl = computed(() =>
  (currentSearch.value.criteriaGroups?.length > 0)
    || (currentSearch.value.globalCriteriaGroup?.criteriaList?.length > 0)
)

const canShowSancareSearchOption = computed(() =>
  (currentSearch.value.privacy === 'sancareSearch' || isAdmin.value)
    && canShareWithSancare(currentSearch.value.type)
)

watch(addSavedSearchRequest, (req) => {
  if (req.error) {
    searchSuccess.value = null
    searchError.value = req.error
  } else if (req.ok) {
    searchError.value = null
    searchSuccess.value = 'Recherche enregistrée'
  }
})

watch(editSavedSearchRequest, (req) => {
  if (req.error) {
    searchSuccess.value = null
    searchError.value = req.error
  } else if (req.ok) {
    searchError.value = null
    searchSuccess.value = 'Modifications enregistrées'
  }
})

onMounted(() => {
  store.dispatch('initStayList')
})

function setParameter(type: string, value: string | number): void {
  store.commit('setStayListParam', { type, value })
  onEdit()
  store.dispatch('refreshStayList', { resetStaySelection: type !== 'pageSize' })
  store.dispatch('refreshStayListUrl', {})
}

function setSearchName(name: string): void {
  store.commit('setSearchName', name)
  onEdit()
}

function setSearchPrivacy(privacy: string): void {
  store.commit('setSearchPrivacy', privacy)
  onEdit()
}

function setSearchCategory(category: string): void {
  if (!userCanEditSearchCategory()) {
    return
  }
  store.commit('setSearchCategory', category)
  onEdit()
}

function handleSearchNameChange(event: Event): void {
  const target = event.target as HTMLInputElement
  setSearchName(target.value)
}

function handleSearchPrivacyChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  setSearchPrivacy(target.value)
}

function handleSearchCategoryChange(event: Event): void {
  const target = event.target as HTMLInputElement
  setSearchCategory(target.value)
}

function addRegularCriteria({ type, value }: { type: string, value: string }): void {
  store.commit('addRegularCriteria', { type, value, criteriaGroupId: null })
  onEdit()
  store.dispatch('refreshStayList', { resetStaySelection: true })
}

function replaceRegularCriteria({ id, type, value }: { id: number, type: string, value: string }): void {
  store.commit('replaceRegularCriteria', {
    type,
    value,
    criteriaGroupId: editingCriterion.value?.criteriaGroupId ?? null,
    id
  })
  onEdit()
  store.dispatch('refreshStayList', { resetStaySelection: true })
  store.dispatch('refreshStayListUrl', {})

  editingCriterion.value = null
}

function removeCriteria({ criteriaGroupId, criterion }: {
  criteriaGroupId: string | number
  criterion: CriterionCondition
}): void {
  emit('clear-message')
  store.commit('removeAdvancedCriteria', { criteriaGroupId, criterion })
  store.dispatch('refreshStayList', {})
  editingCriterion.value = null
}

const startEditCriteria = (condition: ExistingCriterionConditionDto): void => {
  const isSameCriterion = editingCriterion.value?.criterion?.type === condition.criterion.type
    && editingCriterion.value?.criterion?.rawContent === condition.criterion.rawContent

  editingCriterion.value = isSameCriterion ? null : _.cloneDeep(condition)
}

function onEdit(): void {
  searchError.value = null
  searchSuccess.value = null
}

function clearCriteria(): void {
  store.commit('clearCurrentSearchCriteria')
  searchError.value = null

  store.dispatch('refreshStayList', { resetStaySelection: true })
  if (!isRefreshUrl.value) {
    return
  }
  store.dispatch('refreshStayListUrl', {})
}

function resetEdition(): void {
  onEdit()
  store.dispatch('resetEdition', {})
}

function editSelectedSearch(search: Search): void {
  store.commit('loadSavedSearch', search)
  store.commit('setStayListParam', { type: 'searchEdition', value: search.id })
  store.dispatch('refreshStayList', { resetStaySelection: true })
}

async function saveSavedSearch(): Promise<void> {
  if (!isSearchCreation.value && !isSearchEdition.value) {
    searchError.value = 'Aucune recherche en cours d\'édition'
    return
  }

  if (!currentSearch.value.name || currentSearch.value.name === '') {
    searchError.value = 'Nom de la recherche invalide'
    return
  }

  if (!currentSearch.value.privacy || currentSearch.value.privacy === '') {
    searchError.value = 'Niveau de partage incorrect'
    return
  }

  if (!currentSearch.value.savedSearchCategory.name || currentSearch.value.savedSearchCategory.name === '') {
    searchError.value = 'Nom de la catégorie invalide'
    return
  }

  await saveCurrentSavedSearch()

  store.commit('setInitialCriteriaToCurrentSearch')
  store.commit('loadSavedSearch', currentSearch.value)
  store.commit('setEditionInProgress', false)
}

function canDisplayActiveSearchesProperties(): boolean {
  return activeSearches.value.length > 0 && isSearchEdition.value === false
}

function getSearchNameOrCategory(): string {
  if (activeSearches.value.length === 1) {
    return activeSearches.value[0].name
  }
  return ''
}

function userCanEditSearchCategory(): boolean {
  return isSearchEditor.value
      || isOwner.value
      || currentSearch.value.privacy === PrivacyEnum.PUBLIC_SEARCH
}
</script>

<template>
  <div
    name="stayListCriteria"
    class="stay-list-criteria card mb-3"
    :class="{ 'opposite-mode': params.improveRecallMode === true }"
  >
    <div class="card-body">
      <h2
        v-if="params.improveRecallMode === true"
        class="recall-optimisation-mode-title"
      >
        Mode optimisation du rappel
      </h2>
      <div
        v-if="canDisplayActiveSearchesProperties() || isQuickModeAvailable"
        class="d-flex align-items-center"
      >
        <h1
          v-if="canDisplayActiveSearchesProperties()"
        >
          {{ getSearchNameOrCategory() }}
        </h1>
        <div
          v-if="isQuickModeAvailable"
          class="ms-auto d-flex align-items-center"
        >
          <label
            for="quick-mode-toggle"
            class="me-2 text-secondary"
          >
            <small>Aperçu rapide</small>
          </label>
          <sancare-toggle
            id="quick-mode-toggle"
            :value="isQuickModeEnabled"
            @toggle-change="(enabled) => emit('set-quick-mode', enabled)"
          />
        </div>
      </div>
      <stay-list-criteria-parameters
        :mode="mode"
        @edit="onEdit"
        @set-parameter="setParameter"
      />
      <hr v-if="isSearchCreation || isSearchEdition">
      <div v-if="isSearchCreation || isSearchEdition">
        <h2 class="mb-2">
          {{ isSearchCreation ? 'Création' : 'Modification' }} d'une
          {{ searchLabels.singular }}
        </h2>
        <div
          v-if="fetchSavedSearchesRequest.fetching"
          class="loader loader-center"
        />
        <template v-else>
          <div class="row align-items-center">
            <div class="col-5 row">
              <div class="col-5">
                Nom
              </div>
              <div class="col-7">
                <input
                  :value="currentSearch.name"
                  type="text"
                  class="form-control"
                  @change="handleSearchNameChange"
                >
              </div>
            </div>
          </div>
          <div class="row my-1 align-items-center gap-2">
            <div class="col-5 row">
              <div class="col-5">
                Partage
              </div>
              <div class="col-7">
                <select
                  :value="currentSearch.privacy"
                  :disabled="!savedSearchPermissions.canEditPrivacy(currentSearch, currentUser)"
                  class="form-select"
                  @change="handleSearchPrivacyChange"
                >
                  <option value="privateSearch">
                    Privée
                  </option>
                  <option
                    v-if="canShareWithTeam(mode) && isSancareUser"
                    value="teamSearch"
                  >
                    Equipe
                  </option>
                  <option value="publicSearch">
                    Publique
                  </option>
                  <option
                    v-if="canShowSancareSearchOption"
                    :disabled="!isAdmin"
                    value="sancareSearch"
                  >
                    Sancare
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="row my-1 align-items-center">
            <div
              v-if="PrivacyEnum.PRIVATE_SEARCH !== currentSearch.privacy"
              class="col-5 row"
            >
              <div class="col-5">
                Catégorie
              </div>
              <div class="col-7">
                <input
                  :value="currentSearch.savedSearchCategory.name"
                  :disabled="!userCanEditSearchCategory()"
                  type="text"
                  class="form-control"
                  @change="handleSearchCategoryChange"
                >
              </div>
            </div>
          </div>
        </template>
      </div>
      <hr>
      <div>
        <h2 class="mb-2">
          Critères
        </h2>
        <div class="mb-2">
          <filter-builder
            :key="editingCriterion?.criterion?.rawContent ?? 'new'"
            :mode="mode"
            :pmsi-type="pmsiType"
            :is-legacy="true"
            :editing-criterion="editingCriterion"
            @add-criteria="addRegularCriteria"
            @replace-criteria="replaceRegularCriteria"
          />
        </div>
        <div
          v-if="isRefreshUrl"
        >
          <div
            v-if="!currentSearch.isAdvancedSearch"
          >
            <div
              v-if="currentSearch.globalCriteriaGroup !== null"
            >
              <criteria-displayer
                :criteria-group="currentSearch.globalCriteriaGroup"
                :display-grouped-criteria="true"
                :mode="mode"
                internal-operator=""
                :editing-criterion-id="editingCriterion?.criterion.id"
                @remove-criteria="removeCriteria"
                @edit-criteria="startEditCriteria"
              />
              <hr>
            </div>
            <div
              v-for="(criteriaGroup, idx) in currentSearch.criteriaGroups"
              :key="idx"
            >
              <criteria-displayer
                :criteria-group="criteriaGroup"
                :display-grouped-criteria="true"
                :mode="mode"
                internal-operator=""
                :editing-criterion-id="editingCriterion?.criterion.id"
                @edit-criteria="startEditCriteria"
                @remove-criteria="removeCriteria"
              />
            </div>
          </div>
          <div v-else>
            <saved-search-details
              :saved-search="currentSearch"
            />
          </div>
        </div>
      </div>
      <div class="row g-0 align-items-center">
        <div class="col">
          <div
            v-if="searchError"
            class="text-danger"
          >
            {{ searchError }}
          </div>
          <div
            v-else-if="searchSuccess"
            class="text-success"
          >
            {{ searchSuccess }}
          </div>
        </div>
        <div
          v-if="editionInProgress && (isSearchEdition || isSearchCreation)"
          class="col-auto"
        >
          <button
            :disabled="!editionInProgress || !currentSearch.name || !currentSearch.privacy"
            class="btn btn-link"
            @click="saveSavedSearch"
          >
            Enregistrer la {{ searchLabels.singular }}
          </button>
        </div>
        <div class="col-auto">
          <button
            v-if="currentSearchEdited && (isSearchCreation || isSearchEdition)"
            class="btn btn-link"
            @click="resetEdition"
          >
            Annuler les modifications
          </button>
          <button
            v-else-if="isRefreshUrl"
            class="btn btn-link"
            @click="clearCriteria"
          >
            Retirer les critères
          </button>
        </div>
        <div
          v-if="activeSearches.length"
          class="col-auto"
        >
          <show-active-searches-modal
            :active-search-ids="activeSearchIds"
            :pmsi-type="pmsiType"
            :mode="mode"
            @load-saved-search="editSelectedSearch"
          >
            <template #button>
              <button class="btn btn-link">
                {{ activeSearches.length > 1 ? 'Recherches sélectionnées' : 'Recherche sélectionnée' }}
              </button>
            </template>
          </show-active-searches-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h1 {
  font-size: 1.5rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
